// Markdown renderer for memo notes
// Uses marked.js to render .markdown-content elements on page load
// and provides live preview toggle and input toolbar for textarea inputs
(function() {

// Allowed color names (whitelist for XSS safety)
const ALLOWED_COLORS = {
  red: '#dc3545',
  blue: '#007bff',
  green: '#28a745',
  orange: '#fd7e14',
  purple: '#6f42c1',
  gray: '#6c757d'
};

// Escape HTML tags to prevent XSS when rendering user content
const escapeHtml = text => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Convert {color}text{/color} to <span> after markdown rendering
// Only whitelisted color names are allowed
const applyColorTags = html => html.replace(/\{(\w+)\}([\s\S]*?)\{\/\1\}/g, function(match, color, content) {
  const hex = ALLOWED_COLORS[color];
  if (hex) {
    return `<span style="color:${hex}">${content}</span>`;
  } else {
    return match;
  }
});

// Parse markdown and apply color tags
// Note: marked.js handles HTML escaping internally, so we should not
// pre-escape the input. Pre-escaping breaks block-level syntax like
// blockquotes (>) because > becomes &gt; before marked can parse it.
const parseMarkdown = text => applyColorTags(marked.parse(text));

// Insert markdown syntax around selected text or at cursor position
const insertMarkdown = function($textarea, before, after, placeholder) {
  let replacement;
  const el = $textarea[0];
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const text = $textarea.val();
  const selected = text.substring(start, end);

  if (selected.length > 0) {
    replacement = before + selected + after;
  } else {
    replacement = before + placeholder + after;
  }

  $textarea.val(text.substring(0, start) + replacement + text.substring(end));

  // Set cursor position
  if (selected.length > 0) {
    const newPos = start + replacement.length;
    el.setSelectionRange(newPos, newPos);
  } else {
    // Select the placeholder text
    el.setSelectionRange(start + before.length, start + before.length + placeholder.length);
  }

  $textarea.focus();
};

// Insert markdown syntax at the beginning of the current line
const insertLinePrefix = function($textarea, prefix) {
  const el = $textarea[0];
  const start = el.selectionStart;
  const text = $textarea.val();

  // Find the beginning of the current line
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;

  $textarea.val(text.substring(0, lineStart) + prefix + text.substring(lineStart));
  const newPos = start + prefix.length;
  el.setSelectionRange(newPos, newPos);
  $textarea.focus();
};

// Build toolbar HTML
const buildToolbar = function() {
  const buttons = [
    { icon: 'fa-bold',       title: '太字',          action: 'bold' },
    { icon: 'fa-italic',     title: '斜体',          action: 'italic' },
    { icon: 'fa-header',     title: '見出し',        action: 'heading' },
    { icon: 'fa-list-ul',    title: '箇条書き',      action: 'ul' },
    { icon: 'fa-list-ol',    title: '番号付きリスト', action: 'ol' },
    { icon: 'fa-link',       title: 'リンク',        action: 'link' },
    { icon: 'fa-code',       title: 'コード',        action: 'code' },
    { icon: 'fa-quote-left', title: '引用',          action: 'quote' }
  ];
  let html = '<div class="markdown-toolbar">';
  for (const btn of buttons) {
    html += `<button type="button" class="markdown-toolbar-btn" data-md-action="${btn.action}" title="${btn.title}">`;
    html += `<i class="fa ${btn.icon}"></i>`;
    html += '</button>';
  }

  // Color picker dropdown
  html += '<span class="markdown-color-picker">';
  html += '<button type="button" class="markdown-toolbar-btn markdown-color-trigger" title="文字色">';
  html += '<span class="markdown-color-icon"><span class="markdown-color-icon-a">A</span><span class="markdown-color-icon-bar"></span></span>';
  html += '</button>';
  html += '<div class="markdown-color-dropdown">';
  for (const name in ALLOWED_COLORS) {
    const hex = ALLOWED_COLORS[name];
    const label = (() => { switch (name) {
      case 'red': return '赤';
      case 'blue': return '青';
      case 'green': return '緑';
      case 'orange': return 'オレンジ';
      case 'purple': return '紫';
      case 'gray': return 'グレー';
    } })();
    html += `<button type="button" class="markdown-color-option" data-md-color="${name}" title="${label}">`;
    html += `<span class="markdown-color-swatch" style="background-color:${hex}"></span>`;
    html += `<span>${label}</span>`;
    html += '</button>';
  }
  html += '</div></span>';

  html += '</div>';
  return html;
};

// Handle toolbar button clicks
const handleToolbarAction = function($textarea, action) {
  switch (action) {
    case 'bold':    return insertMarkdown($textarea, '**', '**', 'テキスト');
    case 'italic':  return insertMarkdown($textarea, '*', '*', 'テキスト');
    case 'heading': return insertLinePrefix($textarea, '## ');
    case 'ul':      return insertLinePrefix($textarea, '- ');
    case 'ol':      return insertLinePrefix($textarea, '1. ');
    case 'link':    return insertMarkdown($textarea, '[', '](url)', 'リンクテキスト');
    case 'code':    return insertMarkdown($textarea, '`', '`', 'コード');
    case 'quote':   return insertLinePrefix($textarea, '> ');
  }
};

const renderMarkdownElements = function() {
  if (!window.marked) { return; }

  const renderer = new marked.Renderer();

  // Open links in new tab
  renderer.link = function(token) {
    const html = marked.Renderer.prototype.link.call(this, token);
    return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ');
  };

  // Escape raw HTML blocks to prevent XSS
  renderer.html = token => escapeHtml(token.text);

  marked.setOptions({
    breaks: true,
    gfm: true,
    renderer: renderer
  });

  // Render all .markdown-content elements (not preview areas)
  $('.markdown-content').not('.markdown-preview').each(function() {
    const $el = $(this);
    if ($el.data('markdown-rendered')) { return; }
    const rawText = $el.text();
    $el.html(parseMarkdown(rawText));
    $el.data('markdown-rendered', true);
  });

  // Setup toolbars and preview toggles for each markdown input wrapper
  $('.markdown-input-wrapper').each(function() {
    const $wrapper = $(this);
    if ($wrapper.data('markdown-initialized')) { return; }
    $wrapper.data('markdown-initialized', true);

    const $textarea = $wrapper.find('textarea');
    const $preview = $wrapper.find('.markdown-preview');

    // Insert toolbar before textarea's form group
    const $formGroup = $textarea.closest('.form-group');
    if (($formGroup.length > 0) && ($wrapper.find('.markdown-toolbar').length === 0)) {
      $formGroup.before(buildToolbar());
    }

    // Toolbar button click handler
    $wrapper.on('click', '.markdown-toolbar-btn:not(.markdown-color-trigger)', function(e) {
      e.preventDefault();
      const action = $(this).data('md-action');
      handleToolbarAction($textarea, action);
    });

    // Color picker toggle
    $wrapper.on('click', '.markdown-color-trigger', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const $dropdown = $(this).siblings('.markdown-color-dropdown');
      // Close other open dropdowns
      $('.markdown-color-dropdown').not($dropdown).hide();
      $dropdown.toggle();
    });

    // Color option click
    $wrapper.on('click', '.markdown-color-option', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const color = $(this).data('md-color');
      insertMarkdown($textarea, `{${color}}`, `{/${color}}`, 'テキスト');
      $(this).closest('.markdown-color-dropdown').hide();
    });

    // Preview toggle
    $wrapper.find('.markdown-preview-toggle').on('click', function(e) {
      e.preventDefault();
      const $btn = $(this);
      const isJa = (document.documentElement.lang || 'ja').startsWith('ja');
      if ($preview.is(':visible')) {
        $preview.hide();
        $btn.text(isJa ? 'プレビュー' : 'Preview');
      } else {
        $preview.html(parseMarkdown($textarea.val() || ''));
        $preview.show();
        $btn.text(isJa ? '編集に戻る' : 'Back to edit');
      }
    });
  });
};

// Close color dropdown when clicking outside
$(document).on('click', function(e) {
  if (!$(e.target).closest('.markdown-color-picker').length) {
    $('.markdown-color-dropdown').hide();
  }
});

$(document).on('turbo:load', renderMarkdownElements);

})();
